import {
  Button,
  Card,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { FocusableElement } from "@chakra-ui/utils";
import { ErrorBoundaryFallbackProps } from "@suspensive/react";
import { subtract } from "dinero.js";
import {
  FeeChargeEventsSelect,
  getFundFamilyOutstandingFeeChargeEvents,
} from "features/capital-call/FeeChargeEventSelect";
import { FeeSummaryTable } from "features/capital-call/FeeSummaryTable";
import { useFundRetrieve } from "gen/endpoints/fund-entities/fund-entities";
import { FundEntityReadData } from "gen/model/fundEntityReadData";
import { OutstandingFeeChargeEventsByFund } from "gen/model/outstandingFeeChargeEventsByFund";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { QueryBoundary } from "shared/api/QueryBoundary";
import { GenericErrorView } from "shared/components/empty/GenericErrorView";
import {
  formatMoney,
  moneyFromString,
  moneyToString,
  sumMoneyStrings,
} from "shared/utils/MoneyUtils";

export interface CapitalCallFeeModalContentProps
  extends CapitalCallFeeModalCommonProps {
  onClose: () => void;
  inputRef: React.Ref<FocusableElement>;
}

export interface ModalErrorProps
  extends ErrorBoundaryFallbackProps,
    CapitalCallFeeModalCommonProps {
  onClose: () => void;
  fund?: FundEntityReadData;
}

export interface CapitalCallFeeModalCommonProps {
  fundFamilyId: string;
  fundFamilyEventId: string;
  fundId?: string;
  availableEvents: OutstandingFeeChargeEventsByFund;
  feeChargeEventIds: string[] | null | undefined;
  onFeeChargeEventSelect: (
    feeChargeEventIds: string[] | null | undefined,
    totalFeesToCall: Record<string, string>,
  ) => void;
}

export interface CapitalCallFeeModalProps
  extends CapitalCallFeeModalCommonProps {
  onCloseComplete: () => void;
}

export const CapitalCallFeeModal: React.FC<CapitalCallFeeModalProps> = ({
  onCloseComplete,
  ...contentProps
}) => {
  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });
  const inputRef = useRef<FocusableElement>(null);
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onCloseComplete={onCloseComplete}
      initialFocusRef={inputRef}
      closeOnEsc={true}
      isCentered={true}
      size="full"
      closeOnOverlayClick={true}
    >
      <ModalContent>
        <QueryBoundary>
          <CapitalCallFeeModalContent
            {...contentProps}
            onClose={onClose}
            inputRef={inputRef}
          />
        </QueryBoundary>
      </ModalContent>
    </Modal>
  );
};

const CapitalCallFeeModalContent: React.FC<CapitalCallFeeModalContentProps> = ({
  onClose,
  inputRef,
  ...props
}) => {
  const {
    fundFamilyId,
    availableEvents: availableEventData,
    fundId,
    feeChargeEventIds: feeChargeEventIdsProp,
    onFeeChargeEventSelect,
  } = props;
  const { t } = useTranslation(["capitalCall", "common", "errors"]);

  const [selectedFeeChargeEventIds, setSelectedFeeChargeEventIds] = useState(
    feeChargeEventIdsProp ?? undefined,
  );

  const { data: fund } = useFundRetrieve(fundFamilyId, fundId!, {
    query: {
      enabled: !!fundId,
    },
  });

  const availableEvents = fundId
    ? availableEventData.funds[fundId]
    : getFundFamilyOutstandingFeeChargeEvents(availableEventData);

  const { totalOutstanding, outstandingSelectedByFund } = {
    totalOutstanding: sumMoneyStrings(
      availableEvents.map((event) => event.amount_outstanding_to_date),
    ),
    outstandingSelectedByFund: Object.entries(availableEventData.funds).reduce<
      Record<string, string>
    >((acc, [fundId, events]) => {
      const outstanding = sumMoneyStrings(
        events.map((event) =>
          selectedFeeChargeEventIds?.includes(event.id)
            ? event.amount_outstanding_to_date
            : "0.00",
        ),
      );
      return {
        ...acc,
        [fundId]: outstanding,
      };
    }, {}),
  } as const;

  // Flattening arrays to handle both fund_family level and fund level (when a fund is selected, there is only 1 key in outstandingSelectedByFund)
  const totalOutstandingSelectedAmount = sumMoneyStrings(
    Object.values(outstandingSelectedByFund).flat(1),
  );

  const onSave = () => {
    onFeeChargeEventSelect(
      selectedFeeChargeEventIds,
      outstandingSelectedByFund,
    );
    onClose();
  };

  return (
    <>
      <ModalBody p="24px" display="flex" flexDirection="column">
        {fund && <Text textStyle="small">{fund.legal_name}</Text>}
        <Text textStyle="h4" mb="24px">
          {t("common:managementFees")}
        </Text>
        <HStack gap="24px">
          <FeeChargeEventsSelect
            title={t("capitalCall:editor.selectTimePeriods")}
            ref={inputRef}
            value={selectedFeeChargeEventIds ?? undefined}
            onChangeValue={setSelectedFeeChargeEventIds}
            isDisabled={!availableEvents.length}
            availableEvents={availableEvents}
            controlSx={{ maxWidth: "456px" }}
          />
          <Stat mb="16px">
            <StatNumber>{formatMoney(totalOutstanding)}</StatNumber>
            <StatLabel>{t("capitalCall:editor.totalOutstanding")}</StatLabel>
          </Stat>
          <Stat mb="16px">
            <StatNumber>
              ({formatMoney(totalOutstandingSelectedAmount)})
            </StatNumber>
            <StatLabel>{t("capitalCall:editor.outstandingSelected")}</StatLabel>
          </Stat>
          <Stat mb="16px">
            <StatNumber>
              {formatMoney(
                moneyToString(
                  subtract(
                    moneyFromString(totalOutstanding),
                    moneyFromString(totalOutstandingSelectedAmount),
                  ),
                ),
              )}
            </StatNumber>
            <StatLabel>{t("capitalCall:editor.oustandingRemaining")}</StatLabel>
          </Stat>
        </HStack>
        <QueryBoundary
          ErrorComponent={(errorProps) => (
            <ModalError
              {...errorProps}
              {...props}
              fund={fund}
              onClose={onClose}
            />
          )}
        >
          {selectedFeeChargeEventIds?.length ? (
            <FeeSummaryTable
              {...props}
              title={t("capitalCall:editor.tableTitle")}
              feeChargeEventIds={selectedFeeChargeEventIds}
            />
          ) : (
            <Card
              flex={1}
              align="center"
              justifyContent="center"
              backgroundColor="gray.25"
            >
              <Text>
                {t("capitalCall:editor.selectTimePeriodsToSeeFeeBreakdown")}
              </Text>
            </Card>
          )}
        </QueryBoundary>
      </ModalBody>
      <ModalFooter>
        <HStack spacing="10px">
          <Button colorScheme="secondary" onClick={onClose}>
            {t("common:cancel")}
          </Button>
          <Button onClick={onSave}>{t("common:save")}</Button>
        </HStack>
      </ModalFooter>
    </>
  );
};

const ModalError: React.FC<ModalErrorProps> = ({ error, fund }) => {
  const { t } = useTranslation(["common"]);
  return (
    <ModalBody p="24px">
      <Text textStyle="small">
        {fund ? fund.legal_name : t("common:fundFamily")}
      </Text>
      <Text textStyle="h4" mb="24px">
        {t("common:managementFees")}
      </Text>
      <GenericErrorView error={error} />
    </ModalBody>
  );
};
